import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpHeaders, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpContextToken, HttpContext} from '@angular/common/http';
import { EMPTY, Observable, throwError} from 'rxjs';
import { mergeMap, finalize, retryWhen, scan, delay, catchError, filter, takeUntil, defaultIfEmpty } from 'rxjs/operators';
import { AuthenticationService } from './authentication.service';
import { environment } from 'src/environments/environment';
import { CommonService } from '../shared/common.service';

const NO_LOADER_TOKEN = new HttpContextToken(() => false);
/* Use below's noLoaderContextValue as options.context, like "{ headers: this.headers, context: noLoaderContextValue }", for http request if API takes long time and don't want to show laoder */
export const noLoaderContextValue = new HttpContext().set(NO_LOADER_TOKEN, true);

const PASS_NULL_PARAMETER_TOKEN = new HttpContextToken(() => false);
/** Use below's passNUllParameterContextValue as options.context, like `{ headers: this.headers, context: passNUllParameterContextValue }`, for http request where we are passing `null` as a valid parameter. */
export const passNullParameterContextValue = new HttpContext().set(PASS_NULL_PARAMETER_TOKEN, true);

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {

   // Parameters for retry logic
   private maxRetryAttempts = 3;
   private retryDelay = 2000; // 2 seconds
   correlationIdName: string;

  constructor(
    private authenticationService: AuthenticationService, private commonService: CommonService
  ) {
    this.correlationIdName = "X-Correlation-ID";
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const requestNavigationGeneration = this.commonService.getNavigationGeneration();
    const cancelOnNewNavigation$ = this.commonService.navigationCancel$.pipe(
      filter(generation => generation > requestNavigationGeneration)
    );

    // #region - manually preventing/cancelling APIs which are carrying invalid paramters in the URL.
    /* since we are mostly passing paramerter directly in the URL and not in HttpParams splitting params manually */
    let params = request.url.split("?")
    if (params.length > 1) {
        params.splice(0, 1)
        params = params[0].split("&")

        let invalidParams = [undefined, 'undefined', NaN, 'NaN'];
        if (!request.context.get(PASS_NULL_PARAMETER_TOKEN)) {
          /* check for null as invalid parameters in URL if this token is not passed */
          invalidParams = invalidParams.concat([ null, 'null']);
        }

        for (const param of params) {
          if (invalidParams.includes(param.split("=")[1])) {
            return EMPTY;
          };
        }
    }
    // #endregion

    if (this.authenticationService.isLoggingOut) {
      return EMPTY;
    }

    if(!localStorage.getItem('AccessToken')) {
      window.location.href = environment.apiClientPath + '/login';
      return EMPTY;
    } else {
    if (!this.authenticationService.isAuthenticated)
      return next.handle(request);

    const dmsProviderType = localStorage.getItem('DMSProviderType')?.toLowerCase();
      const isDefaultProvider = !dmsProviderType || dmsProviderType === 'default';
      const lowerUrl = request.url.toLowerCase();

      const isTemplateApi = lowerUrl.startsWith('api/dmstemplate/');
      const isProviderDocApi =
        lowerUrl.includes('/documents') ||
        lowerUrl.includes('/kcuser') ||
        lowerUrl.includes('/dms/');

      const isDocAPI = isDefaultProvider && !isTemplateApi && isProviderDocApi;

    const isRepoAPI = request.url.toLowerCase().indexOf("kcdocumentrepository") >= 0 || request.url.toLowerCase().indexOf("kcdocumentprod") >= 0  || request.url.toLowerCase().indexOf("kcdocumentstaging") >= 0 || request.url.toLowerCase().indexOf("kcdmsapp") >= 0;

    if (!request.context.get(NO_LOADER_TOKEN) && !isDocAPI) {
        this.commonService.showLoader();
    }

    return this.authenticationService
      .getAccessToken()
      .pipe(
        takeUntil(cancelOnNewNavigation$),
        defaultIfEmpty(null),
        filter(token => !!token),
        mergeMap(token => {

          let headers = new HttpHeaders({});

          if (localStorage.getItem("Dbkey") != null) {
            headers = headers.append("Dbkey", localStorage.getItem("Dbkey"));
          }

          const isLoggedin = this.authenticationService.isAuthenticated;

          isLoggedin ? this.authenticationService.getAccessToken() : null;

          const isSharePointDownload = request.url.toLowerCase().indexOf("https://kloudconnectau.sharepoint.com/sites") >= 0 ? true : false;

          const shouldAttachAuth = !isRepoAPI;
          const uniqueCorrelationId = this.commonService.get_unique_id();
          const headerObj = {
            ["Authorization"]:
              isLoggedin && isDocAPI
                ? "Bearer " + localStorage.getItem("DMS_Token")
                : `Bearer ${token}`,
            ["Dbkey"]:
              localStorage.getItem("Dbkey") != null
                ? localStorage.getItem("Dbkey")
                : "",
            [this.correlationIdName]: uniqueCorrelationId,
          };

          const authReq = request.clone({
            url:
              isLoggedin && !isDocAPI && !isSharePointDownload && !isRepoAPI
                ? environment.apiBasePath + '/' + request.url
                : request.url,
            setHeaders: shouldAttachAuth ? headerObj : {},
          });

          return next.handle(authReq).pipe(
            takeUntil(cancelOnNewNavigation$),
            retryWhen(errors =>
              errors.pipe(
                scan((retryCount, error) => {
                  retryCount += 1;
                  if (retryCount > this.maxRetryAttempts || !(error instanceof HttpErrorResponse) || error.status !== 0) {
                    throw error;
                  }
                  return retryCount;
                }, 0),
                delay(this.retryDelay)
              )
            ),
            catchError((error: HttpErrorResponse) => {
              return throwError(error);
            }),
            finalize(() => {
              if (!request.context.get(NO_LOADER_TOKEN) && !isDocAPI) {
                this.commonService.hideLoader();
              }
            })
          );
        }));
    }
  }

  private authenticate<T>(request: HttpRequest<T>, token: string): HttpRequest<T> {
    return request.clone({headers: request.headers.set('Authorization', `Bearer ${token}`)});
  }
}
